import {
  Controller,
  Control,
  UseFormGetValues,
  FieldErrors,
} from 'react-hook-form';
import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import { FC, useMemo } from 'react';
import { calculateVariablePrecision, validatePrecision } from 'helpers/drugs';
import { useTranslation } from 'react-i18next';
import DecimalInput from 'components/UI/DecimalInput/DecimalInput';
import ReactTooltip from 'react-tooltip';

/**
 * @description Props for the DrugAmountLimits component
 * @typedef {Object} DrugAmountLimitsProps
 */
type DrugAmountLimitsProps = {
  /** The concentration record being edited or viewed */
  record: Concentration;
  /** React Hook Form control object */
  control: Control<ConcentrationFormData, any>;
  /** Function to get current form values */
  getValues: UseFormGetValues<ConcentrationFormData>;
  /** Form validation errors */
  errors: FieldErrors<ConcentrationFormData>;
  /** Current value of drug amount lower hard limit */
  watchDrugAmountLHL: string | number | null | undefined;
  /** Current value of drug amount lower soft limit */
  watchDrugAmountLSL: string | number | null | undefined;
  /** Current value of drug amount upper soft limit */
  watchDrugAmountUSL: string | number | null | undefined;
  /** Current value of drug amount upper hard limit */
  watchDrugAmountUHL: string | number | null | undefined;
  /** Current selected dose mode unit */
  watchDoseModeUnit:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  /** Whether the user has permission to manage/edit the form */
  canManage: boolean;
};

/**
 * @description Component for managing drug amount limits with validation.
 * Validates order (LHL < LSL < USL < UHL), min/max values, and decimal precision.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {DrugAmountLimitsProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const DrugAmountLimits: FC<DrugAmountLimitsProps> = ({
  record,
  control,
  getValues,
  errors,
  watchDrugAmountLHL,
  watchDrugAmountLSL,
  watchDrugAmountUSL,
  watchDrugAmountUHL,
  watchDoseModeUnit,
  canManage,
}) => {
  const { t } = useTranslation();

  const precisionDrugAmountLHL = useMemo<any>(
    () => calculateVariablePrecision(watchDrugAmountLHL),
    [watchDrugAmountLHL],
  );
  const precisionDrugAmountLSL = useMemo<any>(
    () => calculateVariablePrecision(watchDrugAmountLSL),
    [watchDrugAmountLSL],
  );
  const precisionDrugAmountUSL = useMemo<any>(
    () => calculateVariablePrecision(watchDrugAmountUSL),
    [watchDrugAmountUSL],
  );
  const precisionDrugAmountUHL = useMemo<any>(
    () => calculateVariablePrecision(watchDrugAmountUHL),
    [watchDrugAmountUHL],
  );

  return (
    <div className="row pb-3 flex-nowrap">
      <div className="device-concentration-form__label">
        {t('device.drug_amount')}
      </div>

      {/* Lower hard limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="drug_amount_lower_hard_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LSL, USL, UHL] = getValues([
                  'drug_amount_lower_soft_limit',
                  'drug_amount_upper_soft_limit',
                  'drug_amount_upper_hard_limit',
                ]);

                isValid = isValid && (LSL ? Number(LSL) > currentValue : true);
                isValid = isValid && (USL ? Number(USL) > currentValue : true);
                isValid = isValid && (UHL ? Number(UHL) > currentValue : true);

                return isValid;
              },
              oneLowerLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDrugAmountLSL !== '' &&
                    watchDrugAmountLSL !== null &&
                    !isNaN(watchDrugAmountLSL as any))
                );
              },
              precision: (value) =>
                validatePrecision(value, precisionDrugAmountLHL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDrugAmountLHL}
              rounding={'toAllowedNumberOfDecimals'}
              disabled={!canManage}
              className={
                'device-concentration__input--hard-limit ' +
                (errors?.drug_amount_lower_hard_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="drug_amount_lower_hard_limit"
              data-testid="drug_amount_lower_hard_limit"
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.drug_amount_lower_hard_limit && (
          <ReactTooltip
            id="drug_amount_lower_hard_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.drug_amount_lower_hard_limit?.type ===
              'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.drug_amount_lower_hard_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.drug_amount_lower_hard_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.drug_amount_lower_hard_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.drug_amount_lower_hard_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDrugAmountLHL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Lower soft limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="drug_amount_lower_soft_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LHL, USL, UHL] = getValues([
                  'drug_amount_lower_hard_limit',
                  'drug_amount_upper_soft_limit',
                  'drug_amount_upper_hard_limit',
                ]);

                isValid = isValid && (LHL ? Number(LHL) < currentValue : true);
                isValid = isValid && (USL ? Number(USL) > currentValue : true);
                isValid = isValid && (UHL ? Number(UHL) > currentValue : true);

                return isValid;
              },
              oneLowerLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDrugAmountLHL !== '' &&
                    watchDrugAmountLHL !== null &&
                    !isNaN(watchDrugAmountLHL as any))
                );
              },
              precision: (value) =>
                validatePrecision(value, precisionDrugAmountLSL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDrugAmountLSL}
              rounding={'toAllowedNumberOfDecimals'}
              disabled={!canManage}
              className={
                'device-concentration__input--soft-limit ' +
                (errors?.drug_amount_lower_soft_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="drug_amount_lower_soft_limit"
              data-testid="drug_amount_lower_soft_limit"
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.drug_amount_lower_soft_limit && (
          <ReactTooltip
            id="drug_amount_lower_soft_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.drug_amount_lower_soft_limit?.type ===
              'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.drug_amount_lower_soft_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.drug_amount_lower_soft_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.drug_amount_lower_soft_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.drug_amount_lower_soft_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDrugAmountLSL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Upper soft limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="drug_amount_upper_soft_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LHL, LSL, UHL] = getValues([
                  'drug_amount_lower_hard_limit',
                  'drug_amount_lower_soft_limit',
                  'drug_amount_upper_hard_limit',
                ]);

                isValid = isValid && (LHL ? Number(LHL) < currentValue : true);
                isValid = isValid && (LSL ? Number(LSL) < currentValue : true);
                isValid = isValid && (UHL ? Number(UHL) > currentValue : true);

                return isValid;
              },
              oneUpperLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDrugAmountUHL !== '' &&
                    watchDrugAmountUHL !== null &&
                    !isNaN(watchDrugAmountUHL as any))
                );
              },
              precision: (value) =>
                validatePrecision(value, precisionDrugAmountUSL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDrugAmountUSL}
              rounding={'toAllowedNumberOfDecimals'}
              disabled={!canManage}
              className={
                'device-concentration__input--soft-limit ' +
                (errors?.drug_amount_upper_soft_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="drug_amount_upper_soft_limit"
              data-testid="drug_amount_upper_soft_limit"
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.drug_amount_upper_soft_limit && (
          <ReactTooltip
            id="drug_amount_upper_soft_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.drug_amount_upper_soft_limit?.type ===
              'oneUpperLimitRequired' &&
              t('device.errors.one_upper_limit__required')}
            {errors?.drug_amount_upper_soft_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.drug_amount_upper_soft_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.drug_amount_upper_soft_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.drug_amount_upper_soft_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDrugAmountUSL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Upper hard limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="drug_amount_upper_hard_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LHL, LSL, USL] = getValues([
                  'drug_amount_lower_hard_limit',
                  'drug_amount_lower_soft_limit',
                  'drug_amount_upper_soft_limit',
                ]);

                isValid = isValid && (LHL ? Number(LHL) < currentValue : true);
                isValid = isValid && (LSL ? Number(LSL) < currentValue : true);
                isValid = isValid && (USL ? Number(USL) < currentValue : true);

                return isValid;
              },
              oneUpperLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDrugAmountUSL !== '' &&
                    watchDrugAmountUSL !== null &&
                    !isNaN(watchDrugAmountUSL as any))
                );
              },
              precision: (value) =>
                validatePrecision(value, precisionDrugAmountUHL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDrugAmountUHL}
              rounding={'toAllowedNumberOfDecimals'}
              disabled={!canManage}
              className={
                'device-concentration__input--hard-limit ' +
                (errors?.drug_amount_upper_hard_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="drug_amount_upper_hard_limit"
              data-testid="drug_amount_upper_hard_limit"
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.drug_amount_upper_hard_limit && (
          <ReactTooltip
            id="drug_amount_upper_hard_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.drug_amount_upper_hard_limit?.type ===
              'oneUpperLimitRequired' &&
              t('device.errors.one_upper_limit__required')}
            {errors?.drug_amount_upper_hard_limit?.type ===
              'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.drug_amount_upper_hard_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.drug_amount_upper_hard_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.drug_amount_upper_hard_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.drug_amount_upper_hard_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDrugAmountUHL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Units */}
      <div className="device-concentration-units">
        {/* v8 ignore start -- WAIVER(MICRO): display-only units-label ternary choosing between the persisted record value and the draft form selection; pure formatting artifact with no assertable logic */}
        {record?.is_complete ? record.dose_mode_unit : watchDoseModeUnit?.label}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>
    </div>
  );
};

export default DrugAmountLimits;
